import { useMemo } from 'react';
import MedDayRow from '@/pages/med/MedDayRow';
import { emptyDay, SLOTS } from '@/pages/med/medUtils';

/**
 * Daily View tab body.
 *
 * Layout: two labelled columns — "Date" (left, day boxes stacked) and
 * "Ideal Time" (right, per-slot ideal times aligned into each day box).
 * The visible boxes are, from top to bottom:
 *   1. Today's live row (cascading ON when times are edited / checked)
 *   2. Past days, muted, each with an Edit button that opens the modal
 *
 * Props:
 *   todayIso    – 'YYYY-MM-DD' for today (from ET clock)
 *   todayDay    – full day doc for today (defaults to emptyDay)
 *   pastDays    – array of day docs (sorted newest-first excluding today)
 *   onChangeToday(next) – called on any change to the live row
 *   onEditPast(dayIso)  – called when a past day's Edit button is clicked
 */
export default function MedDailyView({
  todayIso,
  todayDay,
  pastDays,
  onChangeToday,
  onEditPast,
}) {
  const today = useMemo(() => todayDay || emptyDay(todayIso), [todayDay, todayIso]);

  return (
    <div className="w-full max-w-3xl mx-auto" data-testid="med-daily-view">
      {/* Column headers */}
      <div className="grid grid-cols-[1fr_auto] gap-3 px-4 pb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Date
        </div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">
          Ideal Time
        </div>
      </div>

      {/* Today's live row */}
      <MedDayRow
        day={today}
        onChange={onChangeToday}
        mode="live"
      />

      {/* Past days (muted, edit-only via modal) */}
      {pastDays && pastDays.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="med-past-days">
          {pastDays.map((d) => (
            <MedDayRow
              key={d.date}
              day={d}
              mode="past"
              onOpenEdit={() => onEditPast(d.date)}
            />
          ))}
        </div>
      )}

      {(!pastDays || pastDays.length === 0) && (
        <p
          className="mt-6 text-center text-xs text-slate-400"
          data-testid="med-no-past-days"
        >
          No earlier days yet.
        </p>
      )}

      {/* Legend for the four slots (helpful reminder of the schedule). */}
      <div className="mt-6 text-[11px] text-slate-500">
        <div className="font-semibold mb-1">Schedule reference</div>
        <ul className="space-y-0.5">
          <li>• {SLOTS[0].label} — target 1:00 PM ET</li>
          <li>• {SLOTS[1].label} — 5.5 h after Morning Meds was taken</li>
          <li>• {SLOTS[2].label} — 5.5 h after Afternoon Buspar was taken</li>
          <li>• {SLOTS[3].label} — target 2:00 AM ET</li>
        </ul>
      </div>
    </div>
  );
}
